/**
 * Signing in without the cloud (06-flows.md F10 step 1: "PIN login continues from cached
 * hashes"). An offline sign-in makes a LOCAL session: it lets the person work at the till,
 * but the server has never seen it, so it carries no usable token. When the connection is
 * back, the till signs the same person in for real with the PIN they typed (kept in memory
 * only, never stored) and the queue syncs under their own identity. If the page was reloaded
 * meanwhile the PIN is gone: the banner then asks for the PIN again before syncing.
 */
import { createApiClient, type TokenPair, type TokenStore, unwrap } from '@qrkey/api-client';
import { tokens } from '../app/api.js';
import { terminalCredential, terminalStore } from '../app/terminal.js';
import { connectivity } from './connectivity.js';
import { isOfflineSession, OFFLINE_TOKEN, sessionHooks } from './session-core.js';

export { isOfflineSession, OFFLINE_TOKEN };

const FAR = '2999-01-01T00:00:00.000Z';

export interface OfflinePerson {
  userId: string;
  name: string;
  roleId: string;
  roleName: string;
  permissions: readonly string[];
}

let currentPerson: OfflinePerson | null = null;
let memoryPin: { userId: string; pin: string } | null = null;

export function offlinePerson(): OfflinePerson | null {
  const pair = tokens.get();
  return isOfflineSession(pair) && currentPerson?.userId === pair?.session.userId
    ? currentPerson
    : null;
}

/**
 * Sign in locally: the session the app accepts for this person (hand it to the sign-in
 * flow's `finish`), and nothing the server knows about.
 */
export function startOfflineSession(person: OfflinePerson, pin: string): TokenPair {
  const terminal = terminalStore.get();
  if (!terminal) throw new Error('This device is not a registered till');
  currentPerson = person;
  memoryPin = { userId: person.userId, pin };
  return {
    tokenType: 'Bearer',
    accessToken: OFFLINE_TOKEN,
    accessTokenExpiresAt: FAR,
    refreshToken: OFFLINE_TOKEN,
    refreshTokenExpiresAt: FAR,
    session: {
      id: `offline-${person.userId}`,
      userId: person.userId,
      companyId: terminal.companyId,
      surface: 'till',
      authMethod: 'pin',
      terminalId: terminal.terminalId,
      outletId: terminal.outletId,
      mfa: false,
      totpEnrolmentRequired: false,
      expiresAt: FAR,
    },
  } as TokenPair;
}

export function forgetOfflineSession(): void {
  currentPerson = null;
  memoryPin = null;
}

export function hasMemoryPin(): boolean {
  return memoryPin !== null;
}

const memoryTokens = (): TokenStore => {
  let held: TokenPair | null = null;
  return { get: () => held, set: (next) => void (held = next) };
};

let upgrading: Promise<boolean> | null = null;

/**
 * Turn the local session into a real one (single flight). True when the app now holds a
 * server session; false while that is not possible (offline, or the PIN is not in memory).
 */
export function upgradeOfflineSession(): Promise<boolean> {
  const pair = tokens.get();
  if (!isOfflineSession(pair)) return Promise.resolve(true);
  if (!connectivity.isOnline() || memoryPin === null) return Promise.resolve(false);
  upgrading ??= doUpgrade().finally(() => {
    upgrading = null;
  });
  return upgrading;
}

async function doUpgrade(): Promise<boolean> {
  const terminal = terminalStore.get();
  const person = memoryPin;
  if (!terminal || !person) return false;
  try {
    const plain = createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
      tokens: memoryTokens(),
    });
    const fresh = await unwrap(
      plain.client.POST('/v1/auth/pin', {
        body: { terminal: terminalCredential(terminal), userId: person.userId, pin: person.pin },
      }),
    );
    tokens.set(fresh);
    forgetOfflineSession();
    return true;
  } catch {
    // Wrong PIN since, locked, blocked, or the network dropped again: stay local.
    return false;
  }
}

sessionHooks.upgrade = upgradeOfflineSession;
